import { RevisionSingleActionProps } from 'components/tables/data/revision/types';
import { Menu, Tooltip } from '@mantine/core';
import { IconPdf } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import useDocumentGeneratorV2 from 'api/document-generator-v2/use-document-generator-v2';
import { useCallback } from 'react';
import { useApi } from 'api/api-context';
import toBase64 from 'utils/to-base64';
import { useFileManager } from 'api/file-manager/file-manager-context';
import useFileDownload from 'api/use-file-download';
import panic from 'errors/panic';
import { notifications } from '@mantine/notifications';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import { ERROR_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Action performed by the revision technician to accept the revision.
 */
export default function GeneratePdfAction({ revision, kind }: RevisionSingleActionProps) {
  const { getAction } = useApi();
  const { uploadFile } = useFileManager();
  const { generateDraftPdf } = useDocumentGeneratorV2();
  const { download } = useFileDownload();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure();

  const uploadTempFile = useCallback(
    (contents: string | ArrayBuffer | null) =>
      uploadFile({ fileName: `draft-${revision.revisionId}-${Date.now()}.pdf`, contents }),
    [uploadFile, revision.revisionId]
  );

  const generatePdfForRevision = useCallback(() => {
    const fetchRevision = getAction('RevisionGet');

    startLoading();

    notifications.show({
      id: 'generate-pdf',
      title: 'Generovanie PDF',
      message: 'PDF dokument sa generuje...',
      loading: true,
      autoClose: false,
    });

    fetchRevision({ parameters: { revisionId: String(revision.revisionId) } })
      .then((fetchedRevision) => {
        if (!fetchedRevision.revisionTemplate?.isRenderable) {
          notifications.update({
            id: 'generate-pdf',
            title: 'Generovanie PDF',
            message: 'PDF nie je k dispozícii, pretože historická šablóna nemá úplné údaje na vykreslenie.',
            loading: false,
            autoClose: 5000,
          });
          return undefined;
        }

        return generateDraftPdf(fetchedRevision)
          .then(toBase64)
          .then(uploadTempFile)
          .then(download)
          .then(() => {
            notifications.update({
              id: 'generate-pdf',
              title: 'Generovanie PDF',
              message: 'PDF dokument bol vygenerovaný.',
              loading: false,
              autoClose: 3000,
            });
          });
      })
      .catch((error) => {
        notifications.update({
          id: 'generate-pdf',
          title: 'Generovanie PDF',
          message: 'PDF dokument sa nepodarilo vygenerovať.',
          color: ERROR_NOTIFICATION_COLOR,
          loading: false,
          autoClose: 5000,
        });
        panic(error);
      })
      .finally(() => {
        stopLoading();
      });
  }, [revision.revisionId, getAction, generateDraftPdf, uploadTempFile, download]);

  if (kind === 'action-icon') {
    return (
      <Tooltip label="Generovať PDF" events={{ hover: true, focus: true, touch: true }}>
        <ActionIcon variant="primary" size="md" onClick={generatePdfForRevision} disabled={loading}>
          <IconPdf />
        </ActionIcon>
      </Tooltip>
    );
  }

  if (kind === 'button') {
    return (
      <Button
        variant="primary"
        size="md"
        onClick={generatePdfForRevision}
        disabled={loading}
        leftIcon={<IconPdf />}
        w={188}
      >
        Generovať PDF
      </Button>
    );
  }

  return (
    <Menu.Item
      color="gray.8"
      onClick={generatePdfForRevision}
      disabled={loading}
      icon={<IconPdf stroke={1.5} size={20} />}
    >
      Generovať PDF
    </Menu.Item>
  );
}
